import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import * as React from "react"
import { Alert, Checkbox, Form, Input, InputNumber, Radio, Select, Tooltip } from "antd"
import type { SettingFormItem } from "../types"
import { WrappedFormUtils } from "antd/lib/form/Form"
import { getFilteredOptionsForMultiSelect } from "../receiptSettings/utils"
import { InputProps } from "antd/lib/input"
import { FormItemProps } from "antd/lib/form"
import { classNames } from "../../../../utils/classNames"
import { Switch } from "../../../uiKit/Switch/Switch"

export type ValidateStatus = FormItemProps["validateStatus"]

export const formItemLayoutBalanced = {
    colon: false,
    labelCol: {
        xs: {
            span: 12,
        },
        sm: {
            span: 12,
        },
    },
    wrapperCol: {
        xs: {
            span: 12,
        },
        sm: {
            span: 12,
        },
    },
}

export const formItemLayout0to24 = {
    colon: false,
    labelCol: {
        xs: {
            span: 0,
        },
        sm: {
            span: 0,
        },
    },
    wrapperCol: {
        xs: {
            span: 24,
        },
        sm: {
            span: 24,
        },
    },
}

export const formItemLayout2to22 = {
    colon: false,
    labelCol: {
        xs: {
            span: 2,
        },
        sm: {
            span: 2,
        },
    },
    wrapperCol: {
        xs: {
            span: 22,
        },
        sm: {
            span: 22,
        },
    },
}

export const formItemLayout22to2 = {
    colon: false,
    labelCol: {
        xs: {
            span: 22,
        },
        sm: {
            span: 22,
        },
    },
    wrapperCol: {
        xs: {
            span: 2,
        },
        sm: {
            span: 2,
        },
    },
}

export const formItemLayout18to6 = {
    colon: false,
    labelCol: {
        xs: {
            span: 18,
        },
        sm: {
            span: 18,
        },
    },
    wrapperCol: {
        xs: {
            span: 6,
        },
        sm: {
            span: 6,
        },
    },
}

export const formItemLayout8to16 = {
    colon: false,
    labelCol: {
        xs: {
            span: 8,
        },
        sm: {
            span: 8,
        },
    },
    wrapperCol: {
        xs: {
            span: 16,
        },
        sm: {
            span: 16,
        },
    },
}

export const formItemLayout11to13 = {
    colon: false,
    labelCol: {
        xs: {
            span: 11,
        },
        sm: {
            span: 11,
        },
    },
    wrapperCol: {
        xs: {
            span: 13,
        },
        sm: {
            span: 13,
        },
    },
}

export const formItemLayout = {
    colon: false,
    labelCol: {
        xs: {
            span: 14,
        },
        sm: {
            span: 14,
        },
    },
    wrapperCol: {
        xs: {
            span: 10,
        },
        sm: {
            span: 10,
        },
    },
}

export const formItemLayoutMultipleSelection = {
    colon: false,
    labelCol: {
        xs: {
            span: 10,
        },
        sm: {
            span: 10,
        },
    },
    wrapperCol: {
        xs: {
            span: 14,
        },
        sm: {
            span: 14,
        },
    },
}
//  long label, small element

export const formItemLayoutLongLabel = {
    colon: false,
    labelCol: {
        xs: {
            span: 23,
        },
        sm: {
            span: 23,
        },
    },
    wrapperCol: {
        xs: {
            span: 1,
        },
        sm: {
            span: 1,
        },
    },
}
export const formItemLayoutCheckbox = {
    colon: false,
    labelCol: {
        xs: {
            span: 20,
        },
        sm: {
            span: 20,
        },
    },
    wrapperCol: {
        xs: {
            span: 4,
        },
        sm: {
            span: 4,
        },
    },
}

const formItemLayoutSwitch = formItemLayoutCheckbox

const NonTogglingLabel = ({ children }: { children: React.ReactNode }) => (
    <span
        onClick={e => {
            if (!(e.target as HTMLElement).closest("a, button")) {
                e.preventDefault()
            }
        }}>
        {children}
    </span>
)

export type MultiSelectRowFormItemData = {
    key: string
    label: string
}

export const MultiSelectRowFormItem = (
    settingsParam: SettingFormItem,
    key: string,
    label: string,
    form: WrappedFormUtils,
    options: MultiSelectRowFormItemData[],
    selectedKeys: Maybe<string[]>,
    onChangeCallback: (values: string[]) => void,
    className: Maybe<string> = "receipt-setting-form-item"
) => {
    const isIndeterminate = settingsParam[key]?.value === undefined
    const selectedOptions = getFilteredOptionsForMultiSelect(selectedKeys, options).map(option => option.label)
    const filteredOptions = options.filter(option => !(selectedKeys ?? []).includes(option.key))
    return (
        <Form.Item
            // @ts-expect-error
            className={className}
            {...formItemLayoutMultipleSelection}
            label={label}>
            <Select
                mode="multiple"
                style={{ width: "100%" }}
                onChange={onChangeCallback}
                value={selectedOptions}
                placeholder={isIndeterminate ? "Multiple Values" : null}>
                {filteredOptions.map(option => (
                    <Select.Option key={option.key} value={option.label}>
                        {option.label}
                    </Select.Option>
                ))}
            </Select>
        </Form.Item>
    )
}

export const CheckboxFormItem = (
    settingsParam: SettingFormItem,
    key: string,
    label: string | React.ReactNode,
    form: WrappedFormUtils,
    style: Maybe<Record<string, any>>,
    onChangeCallback: (value: boolean) => void,
    className: Maybe<string> = "setting-form-item",
    helperText?: Maybe<string | React.ReactNode>,
    disabled: Maybe<boolean> = false,
    disabledText?: Maybe<string>,
    formLayoutParam?: Maybe<Record<string, any>>,
    helperTextStyle?: Maybe<Record<string, any>>
) => {
    const options = {
        initialValue: settingsParam[key].value,
        valuePropName: "checked",
    }
    const layout = formLayoutParam || formItemLayoutCheckbox

    if (disabled && disabledText) {
        return (
            <div>
                <Form.Item
                    // @ts-expect-error
                    className={className}
                    style={{
                        marginBottom: "0rem",
                        ...style,
                    }}
                    {...layout}
                    label={label}>
                    <Tooltip placement="right" title={disabledText}>
                        {form.getFieldDecorator(`${key}`, options)(<Checkbox disabled={true} checked={false} />)}
                    </Tooltip>
                </Form.Item>
                {helperText && (
                    <div
                        style={{
                            position: "relative",
                            bottom: "0.9rem",
                            paddingLeft: "1rem",
                            paddingRight: "5rem",
                            lineHeight: "1.3",
                            fontSize: "12px",
                            color: "#7E7E7E",
                            ...(helperTextStyle || {}),
                        }}>
                        {helperText}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div>
            <Form.Item
                // @ts-expect-error
                className={className}
                style={{
                    marginBottom: "0rem",
                    ...style,
                }}
                {...layout}
                label={label && <NonTogglingLabel>{label}</NonTogglingLabel>}>
                {form.getFieldDecorator(
                    key,
                    options
                )(
                    <Checkbox
                        onChange={e => {
                            onChangeCallback(e.target.checked)
                        }}
                        indeterminate={form.getFieldValue(key) == null ? settingsParam[key].indeterminate : false}
                    />
                )}
            </Form.Item>
            {helperText && (
                <div
                    style={{
                        position: "relative",
                        bottom: "0.9rem",
                        paddingLeft: "1rem",
                        paddingRight: "5rem",
                        lineHeight: "1.3",
                        fontSize: "12px",
                        color: "#7E7E7E",
                        ...(helperTextStyle || {}),
                    }}>
                    {helperText}
                </div>
            )}
        </div>
    )
}

export const renderSwitchFormItem = (
    settingsParam: SettingFormItem,
    key: string,
    label: string | React.ReactNode,
    form: WrappedFormUtils,
    style: Maybe<Record<string, any>>,
    onChangeCallback: (value: boolean) => void,
    className: Maybe<string> = "setting-form-item",
    disabled: Maybe<boolean> = false,
    disabledText?: Maybe<string>,
    formLayoutParam?: Maybe<Record<string, any>>
) => {
    const layout = formLayoutParam || formItemLayoutSwitch
    const indeterminate = form.getFieldValue(key) == null ? settingsParam[key].indeterminate : false

    const options = {
        initialValue: settingsParam[key].value,
        valuePropName: "checked",
    }

    if (disabled && disabledText) {
        return (
            <div>
                <Form.Item
                    className={classNames(classes.zeroMarginBottom, className)}
                    style={{
                        ...style,
                    }}
                    {...layout}
                    label={label}>
                    <Tooltip placement="right" title={disabledText}>
                        {form.getFieldDecorator(key, options)(<Switch disabled={true} indeterminate={indeterminate} />)}
                    </Tooltip>
                </Form.Item>
            </div>
        )
    }

    return (
        <div>
            <Form.Item
                className={classNames(classes.zeroMarginBottom, className)}
                style={{
                    ...style,
                }}
                {...layout}
                label={label && <NonTogglingLabel>{label}</NonTogglingLabel>}>
                {form.getFieldDecorator(
                    key,
                    options
                )(<Switch indeterminate={indeterminate} onChange={onChangeCallback} />)}
            </Form.Item>
        </div>
    )
}

export const renderRadioButtonGroupFormItem = (
    settingsParam: SettingFormItem,
    key: string,
    label: string,
    form: WrappedFormUtils,
    style: Maybe<Record<string, any>>,
    onChangeCallback: (val: Maybe<string>) => void,
    className: string = "",
    radioButtons: React.ReactNode[],
    helperText?: Maybe<string>,
    doordash?: boolean
) => {
    // eslint-disable-next-line
    let value = doordash
        ? settingsParam[key].value == null || settingsParam[key].value === "DISABLED"
            ? "ON_DEMAND"
            : settingsParam[key].value
        : settingsParam[key].value
    const options = {
        initialValue: value,
    }
    const formItemLayoutRadio = {
        colon: false,
        labelCol: {
            xs: {
                span: 0,
            },
            sm: {
                span: 0,
            },
        },
        wrapperCol: {
            xs: {
                span: 24,
            },
            sm: {
                span: 24,
            },
        },
    }
    return (
        <div>
            {settingsParam[key].indeterminate && (
                <Alert
                    message={
                        <span
                            style={{
                                fontSize: "1rem",
                            }}>
                            Selected Locations have different Ordering Schemes
                        </span>
                    }
                    type="info"
                />
            )}
            <Form.Item
                style={{
                    marginBottom: "0rem",
                    ...style,
                }}
                {...formItemLayoutRadio}
                className={className}
                label={label}>
                {form.getFieldDecorator(
                    key,
                    options
                )(
                    <Radio.Group
                        onChange={e => {
                            if (onChangeCallback) {
                                const val = e && e.target && e.target.value
                                onChangeCallback(val)
                            }
                        }}>
                        {radioButtons.map(r => r)}
                    </Radio.Group>
                )}
            </Form.Item>
            {helperText && (
                <div
                    style={{
                        position: "relative",
                        bottom: "0.9rem",
                        paddingLeft: "1rem",
                        paddingRight: "5rem",
                        lineHeight: "1.3",
                    }}>
                    <span
                        style={{
                            fontSize: "12px",
                        }}>
                        {helperText}
                    </span>
                </div>
            )}
        </div>
    )
}

export const renderInputNumberFormItem = (
    settingsParam: SettingFormItem,
    key: string,
    label: string | React.ReactNode,
    placeholder: string,
    form: WrappedFormUtils,
    style: Maybe<Record<string, any>>,
    onChangeCallback: () => void,
    className: Maybe<string> = "setting-form-item",
    inputStyle?: Maybe<Record<string, any>>,
    validateStatus: ValidateStatus = "success",
    helpMessage?: Maybe<string>
) => {
    const options = {
        initialValue: settingsParam[key].value,
    }
    return (
        <Form.Item
            style={{
                marginBottom: "0rem",
                ...style,
            }}
            className={className || ""}
            {...formItemLayout}
            label={label}
            validateStatus={validateStatus}
            help={helpMessage}>
            {form.getFieldDecorator(
                key,
                options
            )(
                <InputNumber
                    min={0}
                    style={{
                        width: "12rem",
                        marginRight: 0,
                        ...inputStyle,
                    }}
                    onChange={() => onChangeCallback()}
                    placeholder={settingsParam[key].indeterminate ? "Multiple values" : placeholder}
                    onFocus={e => e.target.select()}
                />
            )}
        </Form.Item>
    )
}

export const renderInputFormItem = (
    settingsParam: SettingFormItem,
    key: string,
    label: string,
    placeholder: string,
    form: WrappedFormUtils,
    style: Maybe<Record<string, any>>,
    onChangeCallback: () => void,
    className: Maybe<string> = "setting-form-item",
    helperText?: Maybe<string>,
    disabled?: Maybe<boolean>,
    disabledText?: Maybe<string>,
    inputStyle?: Maybe<Record<string, any>>,
    additionalOptions?: Record<string, any>,
    inputProps?: InputProps,
    helperTextStyle?: Record<string, any>
) => {
    const options = {
        initialValue: settingsParam[key].value,
        ...additionalOptions,
    }

    if (disabled && disabledText) {
        return (
            <div>
                <Form.Item
                    // @ts-expect-error
                    className={className}
                    style={{
                        marginBottom: "0rem",
                        ...style,
                    }}
                    {...formItemLayout}
                    label={`${label}`}>
                    {form.getFieldDecorator(
                        `${key}`,
                        options
                    )(
                        <Tooltip placement="right" title={disabledText}>
                            <Input
                                disabled={disabled}
                                style={{
                                    width: "12rem",
                                    textAlign: "center",
                                }}
                                onChange={() => onChangeCallback()}
                                placeholder={settingsParam[key].indeterminate ? "Multiple values" : placeholder}
                                onFocus={e => e.target.select()}
                                {...inputProps}
                            />
                        </Tooltip>
                    )}
                </Form.Item>
                {helperText && (
                    <div
                        style={{
                            position: "relative",
                            bottom: "0.9rem",
                            paddingLeft: "1rem",
                            paddingRight: "5rem",
                            lineHeight: "1.3",
                            ...(helperTextStyle || {}),
                        }}>
                        <span
                            style={{
                                fontSize: "12px",
                                color: "#7E7E7E",
                            }}>
                            {helperText}
                        </span>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div>
            <Form.Item
                // @ts-expect-error
                className={className}
                style={{
                    marginBottom: "0rem",
                    ...style,
                }}
                {...formItemLayout}
                label={`${label}`}>
                {form.getFieldDecorator(
                    `${key}`,
                    options
                )(
                    <Input
                        disabled={disabled || false}
                        style={{
                            width: "12rem",
                            textAlign: "center",
                            ...inputStyle,
                        }}
                        onChange={() => onChangeCallback()}
                        placeholder={settingsParam[key].indeterminate ? "Multiple values" : placeholder}
                        onFocus={e => e.target.select()}
                        onBlur={e => {
                            const currentValue = e.target.value
                            const trimmedValue = (currentValue || "").trim()
                            if (currentValue !== trimmedValue) {
                                e.target.value = trimmedValue
                                if (inputProps?.onChange) {
                                    inputProps.onChange(e)
                                }
                                form.setFieldsValue({
                                    [key]: trimmedValue,
                                })
                            }
                        }}
                        {...inputProps}
                    />
                )}
            </Form.Item>
            {helperText && (
                <div
                    style={{
                        position: "relative",
                        bottom: "0.9rem",
                        paddingLeft: "1rem",
                        paddingRight: "5rem",
                        lineHeight: "1.3",
                        ...(helperTextStyle || {}),
                    }}>
                    <span
                        style={{
                            fontSize: "12px",
                            color: "#7E7E7E",
                        }}>
                        {helperText}
                    </span>
                </div>
            )}
        </div>
    )
}

export const renderTextAreaFormItem = (
    settingsParam: SettingFormItem,
    key: string,
    label: string,
    placeholder: string,
    form: WrappedFormUtils,
    style: Maybe<Record<string, any>>,
    onChangeCallback: () => void,
    className: Maybe<string> = "setting-form-item"
) => {
    const options = {
        initialValue: settingsParam[key].value,
    }
    return (
        <Form.Item
            // @ts-expect-error
            className={className}
            style={{
                marginBottom: "0rem",
                ...style,
            }}
            {...formItemLayout}
            label={`${label}`}>
            {form.getFieldDecorator(
                `${key}`,
                options
            )(
                <Input.TextArea
                    style={{
                        width: "12rem",
                        textAlign: "center",
                    }}
                    onChange={() => onChangeCallback()}
                    rows={4}
                    placeholder={settingsParam[key].indeterminate ? "Multiple values" : placeholder}
                    onFocus={e => e.target.select()}
                />
            )}
        </Form.Item>
    )
}

export const renderInputOnOffCheckboxFormItem = (
    settingsParam: SettingFormItem,
    key: string,
    stateKey: string,
    label: Maybe<React.ReactNode>,
    helperText: Maybe<string>,
    checked: Maybe<boolean>,
    onChange: (val: boolean) => void,
    style: Maybe<Record<string, any>>,
    className: Maybe<string> = "setting-form-item",
    helperTextStyle: Maybe<Record<string, any>>
) => {
    const settings = Object.assign({}, settingsParam)
    return (
        <div>
            <Form.Item
                // @ts-expect-error
                className={className}
                style={{
                    marginBottom: "0rem",
                    ...style,
                }}
                {...formItemLayout22to2}
                label={label}>
                <Checkbox
                    onChange={e => {
                        onChange(e.target.checked)
                        settings[key].indeterminate = false
                    }}
                    indeterminate={settings[key].indeterminate} // checked={settings[key].indeterminate === true ? undefined : this.state[stateKey]}
                    checked={checked || false}
                />
            </Form.Item>
            {helperText && (
                <div
                    style={{
                        position: "relative",
                        bottom: "0.9rem",
                        paddingLeft: "1rem",
                        paddingRight: "5rem",
                        lineHeight: "1.3",
                        ...(helperTextStyle || {}),
                    }}>
                    <span
                        style={{
                            fontSize: "12px",
                            color: "#7E7E7E",
                        }}>
                        {helperText}
                    </span>
                </div>
            )}
        </div>
    )
}

const classes = {
    zeroMarginBottom: "mb-0",
}
